import { useState } from 'react';
import styles from './ScenarioCards.module.css';
import { personIcon } from '../../assets/icons';
import { ScenarioPersona } from '../../api/types/scenario';

interface ScenarioPersonaCardProps {
  persona?: ScenarioPersona;
  showSwapIcon?: boolean;
}

export function ScenarioPersonaCard({ persona, showSwapIcon = false }: ScenarioPersonaCardProps): JSX.Element {
  return (
    <div className={styles.personaModalWrapper}>
      <div className={`${styles.personaModal} ${showSwapIcon ? styles.withSwapIcon : ''}`}>
        <div className={styles.personaContent}>
          <img 
            src={persona?.icon || personIcon} 
            alt={persona?.display_name || "Default Persona"} 
            className={styles.personaAvatar}
          />
          <div className={styles.personaInfo}>
            <h4 className={styles.personaName}>
              {persona?.display_name || "Default Persona"}
            </h4>
          </div>
        </div>
        {showSwapIcon && (
          <div className={styles.swapIconWrapper}>
            <span className={styles.swapIcon}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7 16L3 12M3 12L7 8M3 12H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M17 8L21 12M21 12L17 16M21 12H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

interface ScenarioDescriptionProps {
  description: string;
}

export function ScenarioDescription({ description }: ScenarioDescriptionProps): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const toggleExpand = () => {
    setExpanded(!expanded);
  };

  const renderDescriptionWithHighlights = (text: string) => {
    // Split the text by both {{user}} and {{char}} patterns
    const parts = text.split(/({{user}}|{{char}})/g);
    
    return parts.map((part, index) => {
      if (part === '{{user}}') {
        return <span key={index} className={styles.highlightedUserToken}>{part}</span>;
      }
      // If the part is {{char}}, render it with the purple color
      if (part === '{{char}}') {
        return <span key={index} className={styles.highlightedCharToken}>{part}</span>;
      }
      // Otherwise, render the text normally
      return <span key={index}>{part}</span>;
    });
  };

  return (
    <div className={styles.scenarioDescription}>
      <div className={styles.descriptionHeader}>
        <h2>Scenario Description</h2>
        <div 
          className={styles.infoIcon}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <span>i</span>
          {showTooltip && (
            <div className={styles.tooltip}>
              This description sets the context and steers the scenario.
            </div>
          )}
        </div>
      </div>
      <div 
        className={expanded ? styles.expandedDescription : styles.collapsedDescription}
        onClick={toggleExpand}
      >
        <p>{renderDescriptionWithHighlights(description)}</p>
      </div>
      <button 
        className={styles.expandButton} 
        onClick={toggleExpand}
      >
        {expanded ? 'Collapse' : 'Expand'}
      </button>
    </div>
  );
} 